import React, { useCallback, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Icons,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './ComposePost.styles';
import { getInitials } from '@app/utils/user';
import { useDispatch } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import useEnabledLTIIntegrations from '@app/hooks/useEnabledLTIIntegrations';
import {
  cleanState,
  clearAllSessionFiles,
  isPersistable,
} from '@ublend-npm/aula-editor';
import { resetAllFileScanStatuses } from '@core/actions/data/fileScan';
import showToast from '@core/toasts/operations/showToast.operation';
import uuid from '@core/utils/uuid';
import feedPostTemplates from './feedPostTemplates';
import isEmptyContent from '../utils/isEmptyContent';
import PostMobileWrapper from '../PostMobile/PostMobileWrapper';
import { ANNOUNCEMENTS } from '@core/constants/postFeedType';
import { getCommonRoomPath } from '@app/actions/navigation';
import pushRoute from '@app/utils/routing';
import { DEFAULT_POST_PLACEHOLDER, POST } from '../text';
import SchedulePostDateTimeDialog from './SchedulePostDateTimeDialog';

const getDefaultScheduleDate = () => new Date(Date.now() + 5 * 60 * 1000);
const getCleanEditorState = () => JSON.parse(JSON.stringify(cleanState));

const PAST_SCHEDULE_DATE_MESSAGE =
  'Choose a future date and time to schedule this post';

export const isFutureScheduleDate = (date: Date, now = Date.now()) =>
  date instanceof Date &&
  Number.isFinite(date.getTime()) &&
  date.getTime() > now;

export type CreatePostAuthor = {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  initials?: string;
};

type ComposePostProps = {
  author?: CreatePostAuthor;
  placeholder?: string;
  isEducator?: boolean;
  isSavingPost: boolean;
  createPost: (postData: any) => Promise<{ objectId?: string } | null | void>;
  spaceId: string;
  editorConfig: any;
  EditorComponent: any;
  feedType: string;
  onBack?: () => void;
};

const ComposePost = ({
  author,
  placeholder = DEFAULT_POST_PLACEHOLDER,
  isSavingPost,
  createPost,
  spaceId,
  editorConfig,
  EditorComponent,
  feedType,
  onBack,
}: ComposePostProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const initials = getInitials({
    firstName: author?.firstName || '',
    lastName: author?.lastName || '',
  });

  const editorRef = useRef(null);
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [scheduleDate, setScheduleDate] = useState(getDefaultScheduleDate);
  const [editorState, setEditorState] = useState(getCleanEditorState);
  const [correlationId, setCorrelationId] = useState<string>(uuid());
  // Check if the screen is mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const commonRoomPath = getCommonRoomPath(spaceId);

  const resetComponentState = () => {
    setEditorState(getCleanEditorState());
    setScheduleDate(getDefaultScheduleDate());
    setCorrelationId(uuid());
    clearAllSessionFiles();
    dispatch(resetAllFileScanStatuses());
  };

  const handleCreatePost = async (
    additionalDataWithSchedulingDate: { scheduledFor?: string } = {}
  ) => {
    try {
      const createdPost = await createPost({
        spaceId,
        content: editorState,
        correlationId,
        feedType,
        ...additionalDataWithSchedulingDate,
      });

      if (
        createdPost === null ||
        (createdPost !== undefined && !createdPost.objectId)
      ) {
        setMessage('The post has not been added');
        return false;
      }

      setMessage('A new post has been added');
      resetComponentState();
      if (isMobile) {
        pushRoute(commonRoomPath);
      }
      return true;
    } catch {
      setMessage('The post has not been added');
      return false;
    }
  };

  const handleSchedulePost = async (date: Date) => {
    if (!isFutureScheduleDate(date)) {
      setMessage(PAST_SCHEDULE_DATE_MESSAGE);
      dispatch(showToast({ message: PAST_SCHEDULE_DATE_MESSAGE }));
      return;
    }

    setScheduleDate(date);
    await handleCreatePost({ scheduledFor: date.toISOString() });
  };

  const filteredEditorConfig = useEnabledLTIIntegrations(editorConfig);
  filteredEditorConfig.correlationId = correlationId;

  const handleEditorChange = useCallback(
    (state) => {
      setEditorState(state);
    },
    [setEditorState]
  );

  const resolvedPlaceholder =
    (feedType === ANNOUNCEMENTS &&
      'Share important information in this space') ||
    placeholder;

  const loading = isSavingPost || isSubmitting;

  const { aulaEditorFtResponsive202512 } = useFlags();
  // Check if the submit button should be disabled. If submit is disabled, show the unsaved changes alert.
  const submitDisabled =
    !isPersistable(editorState, filteredEditorConfig) ||
    loading ||
    isEmptyContent(editorState);

  // Editor component
  const editorComponent = (
    <Box
      sx={{
        ...styles.editorWrapper,
        p: isSavingPost ? '0 0 0 3rem' : '0',
      }}
    >
      <EditorComponent
        key={correlationId}
        type="createpost"
        ref={editorRef}
        config={filteredEditorConfig}
        editorState={editorState}
        placeholder={resolvedPlaceholder}
        onChange={handleEditorChange}
        isSubmitting={loading}
        onFilesStatusChange={({ isUpdating, hasError }) => {
          setUploadInProgress(isUpdating);
          setIsSubmitting(isUpdating && !hasError);
        }}
        readOnly={isSavingPost}
        isAssignmentsEnabled
        templates={feedPostTemplates}
        contentType="post"
        correlationId={correlationId || ''}
        responsiveFlag={aulaEditorFtResponsive202512}
      />
    </Box>
  );

  if (isMobile) {
    return (
      <PostMobileWrapper
        mode="create"
        onBack={onBack}
        onSubmit={() => {
          handleCreatePost();
        }}
        loading={loading || uploadInProgress}
        submitDisabled={submitDisabled}
        showUnsavedChangesAlert={!submitDisabled}
        scheduleAction={
          <SchedulePostDateTimeDialog
            disabled={submitDisabled || uploadInProgress}
            onChange={handleSchedulePost}
            value={scheduleDate}
            renderTrigger={() => (
              <IconButton
                aria-label="Schedule for later"
                disabled={submitDisabled || uploadInProgress}
                size="small"
                sx={styles.scheduleButton}
                type="button"
              >
                <Icons.Schedule aria-hidden="true" />
              </IconButton>
            )}
          />
        }
      >
        {editorComponent}
      </PostMobileWrapper>
    );
  }

  return (
    <Box sx={styles.card}>
      <Box sx={styles.toolbar}>
        <Avatar
          altText={`${author?.firstName || ''} ${
            author?.lastName || ''
          }`.trim()}
          fallbackText={initials}
          kind="circular"
          src={author?.avatarUrl}
          sx={styles.avatar}
        />
        <Box sx={styles.editorContainer}>{editorComponent}</Box>
      </Box>
      <Divider />
      <Box sx={styles.body}>
        <Box sx={styles.actions}>
          <SchedulePostDateTimeDialog
            disabled={submitDisabled || uploadInProgress}
            onChange={handleSchedulePost}
            value={scheduleDate}
            renderTrigger={() => (
              <Tooltip title="Schedule for later">
                <span>
                  <IconButton
                    aria-label="Schedule for later"
                    disabled={submitDisabled || uploadInProgress}
                    size="small"
                    sx={styles.scheduleButton}
                    type="button"
                  >
                    <Icons.Schedule aria-hidden="true" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          />
          <Button
            kind="primary"
            type="button"
            loading={loading || uploadInProgress}
            disabled={submitDisabled || uploadInProgress}
            sx={styles.postButton}
            onClick={() => handleCreatePost()}
          >
            {/* Both label and loader are overlapped when loading is true. to fix it */}
            {loading || uploadInProgress ? '' : POST}
          </Button>
          <Box sx={styles.visuallyHidden} aria-live="polite" role="status">
            {message}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ComposePost;
